import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { CalendarCheck, MessageSquare, PhoneCall, ArrowRight } from 'lucide-react';
import { PUBLIC_BOOKING_ENABLED } from '../config';
import { useLocalizedUrl } from '../utils/urlHelper';
import SeoMeta from '../components/SeoMeta';
import ContactChannels from '../components/common/ContactChannels';
import { PageBand } from '../components/common/B2B';

// Contacto (nov-2026): diseño B2B y tratamiento de usted. Tres vías (demo,
// formulario, llamada) más teléfono, horario y correo. Los parámetros de la URL
// (?origin=…&profile=…) se pasan a las tres vías, que los usan para titular y
// cualificar.

const TEXTS = {
  es: {
    eyebrow: 'Contacto',
    title: 'Hable con nosotros',
    subtitle: 'Elija cómo prefiere contactar con nuestro equipo.',
    demoTitle: 'Reserve su demostración',
    demoDesc: 'Vea BlueCMR aplicado a su operativa. Le confirmamos la cita por correo.',
    formTitle: 'Escríbanos',
    formDesc: 'Envíenos su consulta y le respondemos.',
    callTitle: 'Le llamamos',
    callDesc: 'Déjenos su teléfono y le llamamos en horario de atención.',
    seoTitle: 'Contacto — BlueCMR',
    seoDescription: 'Reserve una demostración, envíenos su consulta o pida que le llamemos. Teléfono y horario de atención de BlueCMR.',
  },
  en: {
    eyebrow: 'Contact',
    title: 'Talk to us',
    subtitle: 'Choose how you prefer to reach our team.',
    demoTitle: 'Book your demonstration',
    demoDesc: 'See BlueCMR applied to your operations. We confirm the appointment by email.',
    formTitle: 'Write to us',
    formDesc: 'Send us your enquiry and we will get back to you.',
    callTitle: 'We call you',
    callDesc: 'Leave your phone number and we will call you during business hours.',
    seoTitle: 'Contact — BlueCMR',
    seoDescription: 'Book a demonstration, send us your enquiry or request a call. BlueCMR phone number and business hours.',
  },
};

const Option = ({ to, icon: Icon, title, desc }) => (
  <Link to={to} className="group flex flex-col border-t-2 border-slate-900 pt-6 pb-2">
    <Icon className="w-7 h-7 text-primary-700 mb-5" strokeWidth={1.5} />
    <h2 className="text-xl font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{title}</h2>
    <p className="mt-2 flex-1 text-[15px] text-slate-600 leading-relaxed">{desc}</p>
    <ArrowRight className="mt-5 w-5 h-5 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />
  </Link>
);

const ContactHubPage = () => {
  const { i18n } = useTranslation();
  const { getLocalizedUrl } = useLocalizedUrl();
  const { search } = useLocation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const t = TEXTS[lang];
  const link = (path) => `${getLocalizedUrl(path)}${search}`;

  return (
    <div className="bg-white">
      <SeoMeta
        title={t.seoTitle}
        description={t.seoDescription}
        canonical="https://www.bluecmr.com/contacto"
        alternates={{
          es: 'https://www.bluecmr.com/contacto',
          en: 'https://www.bluecmr.com/en/contact',
          'x-default': 'https://www.bluecmr.com/contacto'
        }}
        breadcrumbs={[
          { name: lang === 'es' ? 'Inicio' : 'Home', url: 'https://www.bluecmr.com/' },
          { name: lang === 'es' ? 'Contacto' : 'Contact', url: 'https://www.bluecmr.com/contacto' },
        ]}
      />

      <PageBand eyebrow={t.eyebrow} title={t.title} text={t.subtitle} />

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid gap-x-10 gap-y-12 ${PUBLIC_BOOKING_ENABLED ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            {PUBLIC_BOOKING_ENABLED && (
              <Option to={link('/contacto/demo')} icon={CalendarCheck} title={t.demoTitle} desc={t.demoDesc} />
            )}
            <Option to={link('/contacto/formulario')} icon={MessageSquare} title={t.formTitle} desc={t.formDesc} />
            <Option to={link('/contacto/llamada')} icon={PhoneCall} title={t.callTitle} desc={t.callDesc} />
          </div>

          <div className="mt-16 pt-12 border-t border-slate-200">
            <ContactChannels lang={lang} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactHubPage;
